import React, { useEffect, useReducer, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import {
  LOGIN_AND_CLOSE_URL,
  HTTP_RESPONSE_STATUS_FORBIDDEN_IN_THIS_MONTH,
} from "../../../config";
import { getLoginStatus } from "../../../api/sso";
import {
  getEventRecords,
  downloadFilteredEventRecord,
} from "../../../api/kquake";
import { convertTimestampToKstStr } from "../../../utils";

import { setLoginStatus } from "../../../store/userSlice";
import { selectAllStations } from "../../../store/stationsSlice";
import {
  EarthquakeFilterContext,
  earthquakeFilterDefaultState,
  earthquakeFilterReducer,
  getKQuakeEarthquakeApiFilter,
} from "../../../contexts/EarthquakeFilterContext";
import {
  StationFilterContext,
  stationFilterDefaultState,
  stationFilterReducer,
  selectStations,
} from "../../../contexts/StationFilterContext";
import {
  EventRecordFilterContext,
  eventRecordFilterDefaultState,
  eventRecordFilterReducer,
  getKQuakeEventRecordApiFilter,
} from "../../../contexts/EventRecordFilterContext";

import { EventRecordDetailsModal } from "../../modals/EventRecordDetailsModal";
import { EventRecordProcessModal } from "../../modals/EventRecordProcessModal";
import { EarthquakeSelectorForm } from "../../forms/EarthquakeSelectorForm";
import { StationSelectorForm2 } from "../../forms/StationSelectorForm2";
import { EventRecordSelectorForm } from "../../forms/EventRecordSelectorForm";
import { EventRecordsTable } from "../../EventRecordsTable";
import { LoadingScreen } from "../../LoadingScreen";
import { Trans, useTranslation } from "react-i18next";

const PRUPOSES_OF_USE = [
  "study_natural_science",
  "study_engineering",
  "seismic_desing",
  "educational",
  "etc",
];

const PROCESSED_DATA_CONTENTS = ["acc", "vel", "disp", "acc_spectra"];

export const KEsmDB = () => {
  const stations = useSelector(selectAllStations);
  const isLoggedIn = useSelector((state) => state.user.login);
  const apiToken = useSelector((state) => state.user.apiToken);

  const [earthquakeFilter, dispatchEarthquakeFilter] = useReducer(
    earthquakeFilterReducer,
    earthquakeFilterDefaultState
  );
  const [stationFilter, dispatchStationFilter] = useReducer(
    stationFilterReducer,
    stationFilterDefaultState
  );
  const [eventRecordFilter, dispatchEventRecordFilter] = useReducer(
    eventRecordFilterReducer,
    eventRecordFilterDefaultState
  );

  const [downloadDataType, setDownloadDataType] = useState("processed"); // 'raw' or 'processed'
  const [downloadDataContents, setDownloadDataContents] = useState(["acc"]); // 'raw' or 'processed'
  // const [downloadDataFormat, setDownloadDataFormat] = useState('text')  // 'text' or 'mseed'
  const [downloadPurpose, _setDownloadPurpose] = useState(""); // 'study', 'business', 'educational', or 'etc'
  const [agreeToTermsOfUse, setAgreeToTermsOfUse] = useState(false);

  const setDownloadPurpose = (purpose) => {
    window.sessionStorage.setItem("downloadPurpose", purpose);
    _setDownloadPurpose(purpose);
  };

  useEffect(() => {
    const purpose = window.sessionStorage.getItem("downloadPurpose");
    if (purpose in PRUPOSES_OF_USE) {
      setDownloadPurpose(purpose);
    }
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [eventRecords, setEventRecords] = useState([]);
  const [selectedEventRecords, setSelectedEventRecords] = useState([]);
  const [eventRecordToShowDetails, setEventRecordToShowDetails] =
    useState(null);
  const [eventRecordToProcess, setEventRecordToProcess] = useState(null);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const searchRecords = async () => {
    let earthquakeApiFilter = {};
    try {
      earthquakeApiFilter = getKQuakeEarthquakeApiFilter(earthquakeFilter);
    } catch (error) {
      alert(
        t("form.message.errorInEvent") +
          "\r\n" +
          t("form.message." + error.message)
      );
      return;
    }

    let selectedStations = [];
    try {
      selectedStations = selectStations(stations, stationFilter);
    } catch (error) {
      alert(
        t("form.message.errorInStation") +
          "\r\n" +
          t("form.message." + error.message)
      );
      return;
    }

    if (selectedStations.length === 0) {
      alert(t("form.message.noStations"));
      return;
    }

    let eventRecordApiFilter = {};
    try {
      eventRecordApiFilter = getKQuakeEventRecordApiFilter(eventRecordFilter);
    } catch (error) {
      alert(
        t("form.message.errorInEventRecord") +
          "\r\n" +
          t("form.message." + error.message)
      );
      return;
    }

    setEventRecords([]);
    setSelectedEventRecords([]);

    const apiFilter = { ...earthquakeApiFilter, ...eventRecordApiFilter };
    if (selectedStations.length !== stations.length) {
      apiFilter.station = selectedStations.map(({ code }) => code).join(",");
    }

    let responseData = {};
    setIsLoading(true);
    try {
      responseData = await getEventRecords(apiFilter);
    } catch (error) {
      setIsLoading(false);
      if (error.httpStatus === 204) {
        alert(t("form.message.noEvents"));
      } else {
        const alertMessage = [
          t("form.message.failedToRetrieve"),
          `Error message:${error.message}`,
          t("form.message.contactAdministrator"),
        ];
        alert(alertMessage.join("\r\n"));
      }
      return;
    }
    setIsLoading(false);

    const { eventRecords, earthquakeDetails, stationDetails } = responseData;
    if (eventRecords) {
      eventRecords.forEach((record) => {
        record.streamId = `${record.network}.${record.station}.${record.location}.${record.channel}`;
        record.id = `${record.eventId}_${record.streamId}`;
        record.sensorType = record.sensorDepth > 0 ? "borehole" : "surface";

        if (
          earthquakeDetails &&
          earthquakeDetails.hasOwnProperty(record.eventId)
        ) {
          const { originTime, ML, latitude, longitude, depth } =
            earthquakeDetails[record.eventId];
          record.eventOriginTime = originTime;
          record.eventOriginTimeStrKST = convertTimestampToKstStr(originTime);
          record.evnetMagnitudeML = ML;
          record.evnetLatitude = latitude;
          record.evnetLongitude = longitude;
          record.evnetDepth = depth;
        } else {
          alert(
            t("page.kesmdb.noEventInfoWarning", { eventId: record.eventId })
          );
        }
        if (stationDetails && stationDetails.hasOwnProperty(record.station)) {
          const { name, latitude, longitude, elevation, site_class } =
            stationDetails[record.station];
          record.stationName = name;
          record.stationCode = record.station;
          record.stationLatitude = latitude;
          record.stationLongitude = longitude;
          record.stationElevation = elevation;
          if (site_class) {
            const { vs30, h_and_vs_soil } = site_class;
            if (vs30) {
              record.siteClassVs30 =
                vs30["Downhole test"] ||
                vs30["HVSR estimation"] ||
                vs30["P-wave estimation"] ||
                "";
            }
            if (h_and_vs_soil) {
              record.siteClassHVSSoil = h_and_vs_soil["Downhole test"] || "";
            }
          }
        } else {
          alert(
            t("page.kesmdb.noStationInfoWarning", {
              stationCode: record.station,
            })
          );
        }
      });
      setEventRecords(eventRecords);
    }

    if (responseData.overflow) {
      alert(t("form.message.someSearchResultsMissingWarning"));
    }
  };

  const downloadData = async (event) => {
    event.stopPropagation();
    if (selectedEventRecords.length === 0) {
      alert(t("form.message.selectDataToDownload"));
      return;
    }
    if (!downloadDataType) {
      alert(t("form.message.selectDataTypeToDownload"));
      return;
    }
    // if (!downloadDataFormat) {
    //   alert('다운로드 받을 자료의 형식(포멧)을 선택해주세요.')
    //   return
    // }
    if (downloadDataType === "processed" && downloadDataContents.length === 0) {
      alert(t("form.message.selectDataContentTypeToDownload"));
      return;
    }
    if (!downloadPurpose) {
      alert(t("form.message.selectPurpose"));
      return;
    }
    if (!agreeToTermsOfUse) {
      alert(t("form.message.agreeToTermsOfUse"));
      return;
    }

    let logInStatus = {};
    if (!isLoggedIn) {
      setIsLoading(true);
      logInStatus = await getLoginStatus();
      if (logInStatus?.status === 200) {
        dispatch(setLoginStatus({ ...logInStatus }));
      }
      setIsLoading(false);
    }

    if (!isLoggedIn && logInStatus?.status !== 200) {
      if (window.confirm(t("form.message.clickDownloadAfterLogin"))) {
        const loginWindow = window.open(LOGIN_AND_CLOSE_URL, "_blank");
        if (loginWindow) {
          loginWindow.focus();
        }
      }
      return;
    }

    const _apiToken =
      logInStatus?.status === 200 ? logInStatus.token : apiToken;
    // const _apiToken = '__test__'
    const requestTimestamp = Date.now() / 1000;
    const downloadDataFormat = downloadDataType === "raw" ? "mseed" : "text";
    setIsLoading(true);
    const response = await downloadFilteredEventRecord(
      downloadDataType,
      downloadDataType === "processed" ? downloadDataContents : null,
      downloadDataFormat,
      downloadPurpose,
      selectedEventRecords,
      _apiToken
    );
    if (response.status === 200) {
      const fileName = `kquake_eventdata_download_${requestTimestamp.toFixed(
        0
      )}.zip`;
      const blob = await response.blob();
      setIsLoading(false);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.setAttribute("download", fileName);
      a.click();
    } else {
      setIsLoading(false);
      if (response.status === 204) {
        alert(
          t("form.message.noDataToDownload") +
            "\r\n" +
            t("form.message.contactAdministrator")
        );
      } else if (response.status === 401) {
        alert(t("form.message.authenticationFailedRefresh"));
      } else if (
        response.status === HTTP_RESPONSE_STATUS_FORBIDDEN_IN_THIS_MONTH
      ) {
        alert(t("form.message.downloadCapacityExceeded"));
      } else {
        const alertMessage = [
          t("form.message.failedToDownload"),
          `Error message:${response.statusText}`,
          t("form.message.contactAdministrator"),
        ];
        alert(alertMessage.join("\r\n"));
      }
    }
  };

  const [eventRecordDataCache, setEventRecordDataCache] = useState({});

  return (
    <div className="container">
      <div className="sub-container">
        <div style={{ fontSize: "15px", marginBottom: "5px" }}>
          &nbsp;&nbsp;<i className="ion ion-md-help-circle-outline"></i>
          &nbsp;{t("page.kesmdb.desc")}
        </div>
        <div className="overflow-auto">
          <table className="datatable">
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "80%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th className="first position-relative">
                  {t("w.event")}
                  <div className="quote-text ">
                    <i className="ion ion-md-information-circle-outline"></i>{" "}
                    {t("page.kesmdb.eventDesc")}
                  </div>
                </th>
                <td className="first">
                  <EarthquakeFilterContext.Provider
                    value={{
                      state: earthquakeFilter,
                      dispatch: dispatchEarthquakeFilter,
                    }}
                  >
                    <EarthquakeSelectorForm />
                  </EarthquakeFilterContext.Provider>
                </td>
              </tr>
              <tr>
                <th className="">{t("w.station")}</th>
                <td className="">
                  {/* <AdvancedStationSelectorForm onChange={setStationSelector} /> */}
                  <StationFilterContext.Provider
                    value={{
                      state: stationFilter,
                      dispatch: dispatchStationFilter,
                    }}
                  >
                    <StationSelectorForm2 />
                  </StationFilterContext.Provider>
                </td>
              </tr>
              <tr>
                <th className="position-relative">
                  {t("page.kesmdb.eventRecord")}
                  <div className="quote-text">
                    <i className="ion ion-md-information-circle-outline"></i> 1
                    g ≈ 981 cm/s<sup>2</sup>
                  </div>
                </th>
                <td>
                  <EventRecordFilterContext.Provider
                    value={{
                      state: eventRecordFilter,
                      dispatch: dispatchEventRecordFilter,
                    }}
                  >
                    <EventRecordSelectorForm />
                  </EventRecordFilterContext.Provider>
                </td>
              </tr>
            </tbody>
          </table>
          <br />
          <div className="center-align row">
            <button
              type="button"
              className="btn-block waves-effect waves-light button-lg"
              onClick={searchRecords}
            >
              {t("w.search")}
            </button>
          </div>
        </div>
        <EventRecordsTable
          eventRecords={eventRecords}
          selectedEventRecords={selectedEventRecords}
          setSelectedEventRecords={setSelectedEventRecords}
          setEventRecordToShowDetails={setEventRecordToShowDetails}
          setEventRecordToProcess={setEventRecordToProcess}
        />
        <div className="service-explanation">
          &nbsp;&nbsp;<i className="ion ion-md-information-circle-outline"></i>
          &nbsp;
          <Trans
            i18nKey="page.kesmdb.processedDataDesc"
            components={{
              link1: (
                <a
                  href="https://kr.object.gov-ncloudstorage.com/kerc-kquake-public/manuals/K_EMS_DB_manual_latest.pdf"
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </div>
        <div className="dataselect_box">
          <span className="txt_title">{t("w.data")}</span>
          <form className="inline-block">
            <label>
              <input
                className="with-gap"
                name="datatype"
                type="radio"
                checked={downloadDataType === "processed"}
                onChange={(e) => {
                  if (e.target.checked) {
                    setDownloadDataType("processed");
                  }
                }}
              />
              <span>{t("page.kesmdb.processedData")} (ASCII)</span>
            </label>
            <label>
              <input
                className="with-gap"
                name="datatype"
                type="radio"
                checked={downloadDataType === "raw"}
                onChange={(e) => {
                  if (e.target.checked) {
                    setDownloadDataType("raw");
                  }
                }}
              />
              <span>{t("page.kesmdb.rawData")} (miniSEED)</span>
            </label>
          </form>
        </div>
        {downloadDataType === "processed" && (
          <div className="dataselect_box">
            <span
              className="hide-on-small-and-down"
              style={{ whiteSpace: "pre" }}
            >
              {" "}
            </span>
            <form className="inline-block">
              {PROCESSED_DATA_CONTENTS.map((contentType) => (
                <label key={contentType}>
                  <input
                    className="filled-in"
                    name="datatype"
                    type="checkbox"
                    checked={downloadDataContents.includes(contentType)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setDownloadDataContents([
                          ...downloadDataContents,
                          contentType,
                        ]);
                      } else {
                        setDownloadDataContents(
                          downloadDataContents.filter((c) => c !== contentType)
                        );
                      }
                    }}
                  />
                  <span>
                    {t("page.kesmdb.processedDataContent." + contentType)}
                  </span>
                </label>
              ))}
            </form>
          </div>
        )}
        <div className="dataselect_box">
          <span className="txt_title">
            {t("w.purposeOfUse")}&nbsp;&nbsp;&nbsp;&nbsp;
          </span>
          <form className="inline-block">
            {PRUPOSES_OF_USE.map((purpose) => (
              <label key={purpose}>
                <input
                  className="with-gap"
                  name="dataformat"
                  type="radio"
                  checked={downloadPurpose === purpose}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setDownloadPurpose(purpose);
                    }
                  }}
                />
                <span>{t("purposeOfUse." + purpose)}</span>
              </label>
            ))}
          </form>
        </div>
        <div className="center-align row">
          <div className="col l12 mb20">
            <label className="datatable_select_radio">
              <input
                name="stationinformation"
                className="filled-in"
                type="checkbox"
                checked={agreeToTermsOfUse}
                onChange={(e) => setAgreeToTermsOfUse(e.target.checked)}
              />
              <span>
                <Trans
                  i18nKey="page.kesmdb.agreement"
                  components={{
                    link1: (
                      <Link
                        to="/intro/terms"
                        target="_blank"
                        rel="noreferrer"
                      />
                    ),
                  }}
                />
              </span>
            </label>
          </div>
          {/* <form method="get" action="./data/_sample-img-5.zip"> */}
          <button
            type="button"
            className="btn-block waves-effect waves-light button-lg"
            onClick={downloadData}
          >
            {t("w.download")} ({selectedEventRecords.length})
          </button>
          {/* </form> */}
        </div>
      </div>
      {eventRecordToShowDetails && (
        <EventRecordDetailsModal
          record={eventRecordToShowDetails}
          onClose={() => setEventRecordToShowDetails(null)}
          dataCache={eventRecordDataCache}
          setDataCache={setEventRecordDataCache}
        />
      )}
      {eventRecordToProcess && (
        <EventRecordProcessModal
          record={eventRecordToProcess}
          onClose={() => setEventRecordToProcess(null)}
          dataCache={eventRecordDataCache}
          setDataCache={setEventRecordDataCache}
          setIsLoading={setIsLoading}
          downloadPurpose={downloadPurpose}
          setDownloadPurpose={setDownloadPurpose}
        />
      )}
      <LoadingScreen screen={isLoading} />
    </div>
  );
};
